import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Compass,
  MessagesSquare,
  Search,
  Bell,
  User,
  Settings as SettingsIcon,
  Plus,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { yLogoInline } from "@/assets/brand/yLogoInline";

/**
 * PublicProfileGate — wraps the `/:username` and `/:username/(followers|
 * following)` routes so they're viewable WITHOUT an account.
 *
 * Product requirement (2026-02): a first-time visitor tapping a shared
 * profile link should immediately see the profile they came for — NOT
 * a login redirect and NOT an onboarding step for someone who never
 * signed up. If they're not authed, we give them 10 seconds to browse
 * the profile, then float a small "Log in or Sign up" prompt that,
 * either way, sends them to the pre-signup Landing (`/`) — never to
 * `/onboarding/*`.
 *
 * Authed users pass through the standard AppShell (sidebar + right
 * rail on desktop, bottom tabs on mobile). They ALSO bypass the usual
 * onboarding redirect for THIS route only, so someone mid-onboarding
 * can still open a shared profile without being punted back to their
 * next onboarding step.
 */
const POPUP_DELAY_MS = 10_000;

export default function PublicProfileGate({ children }) {
  const { authed, loading } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  const goToLanding = () => navigate("/");

  useEffect(() => {
    if (loading || authed) return;
    // 10-second passive timer — appears even if the guest just scrolls
    // and never interacts.
    const t = setTimeout(() => setShowPopup(true), POPUP_DELAY_MS);
    return () => clearTimeout(t);
  }, [authed, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <div className="text-sm">Loading…</div>
      </div>
    );
  }

  // Signed-in users see the profile inside the normal app chrome. We
  // intentionally DO NOT run the RequireAuth onboarding-redirect chain
  // here so someone mid-onboarding can still open a shared profile.
  if (authed) return <AppShell>{children}</AppShell>;

  // Guest view — minimal chrome, profile visible, and ANY meaningful
  // interaction (tap on a photo, switch tab, follow button, stat row,
  // like/save/comment, etc.) opens the sign-in dialog immediately AND
  // suppresses the underlying navigation so guests don't get bounced
  // to an auth-required page (`/post/:id`, `/:username/followers`, …).
  const openPopupOnInteraction = (e) => {
    // Ignore clicks inside the top bar — its Sign-up CTA already
    // navigates to Landing.
    if (e.target.closest("[data-testid='guest-topbar']")) return;
    // Let the popup's own controls (log in / sign up / dismiss / X /
    // backdrop) work normally.
    if (e.target.closest("[data-testid='guest-signin-sheet']")) return;
    if (e.target.closest("[data-testid='guest-signin-backdrop']")) return;
    // Right rail's Log in / Sign up card + top-bar Sign up: these are
    // legitimate conversion CTAs, not exploratory clicks — let them
    // through so they can store the deep link and route to Landing.
    if (e.target.closest("[data-testid='guest-rail-login']")) return;
    if (e.target.closest("[data-testid='guest-rail-signup']")) return;
    if (e.target.closest("[data-testid='guest-topbar-cta']")) return;
    // Show the popup and block the underlying behavior so the guest
    // stays on the profile.
    setShowPopup(true);
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className="min-h-[100dvh] bg-background text-foreground"
      onClickCapture={openPopupOnInteraction}
      onKeyDownCapture={(e) => {
        if (e.key === "Tab") return;
        openPopupOnInteraction(e);
      }}
    >
      {/*
        Desktop (>= lg): 3-column shell that mirrors the authed
        `AppShell` — left nav rail, center feed column, right rail
        with a Search bar + "Join BrontoY" card. Same visual weight
        so the guest view feels like the real product, not a shrunk
        window. Every nav item is decorative for guests: our
        `openPopupOnInteraction` capture-listener already suppresses
        clicks and pops the sign-in sheet.

        Mobile (< lg): unchanged from before — sticky `GuestTopBar`
        with logo + Sign up CTA, centred 600 px column below.
      */}
      {/* Mobile-only top bar */}
      <div className="lg:hidden">
        <GuestTopBar onCta={goToLanding} />
      </div>

      {/* Desktop 3-column shell */}
      <div className="hidden lg:grid min-h-[100dvh] mx-auto max-w-[1320px] px-6" style={{ gridTemplateColumns: "260px minmax(0,1fr) 340px", gap: "24px" }}>
        <GuestLeftNav onCta={goToLanding} />
        <main className="min-w-0 py-6" data-testid="guest-profile-main-desktop">
          {children}
        </main>
        <aside className="py-6 sticky top-0 self-start" data-testid="guest-right-rail">
          <GuestRightRail
            onLogin={goToLanding}
            onSignup={goToLanding}
          />
        </aside>
      </div>

      {/* Mobile column */}
      <main
        className="lg:hidden w-full max-w-[600px] mx-auto pt-2"
        data-testid="guest-profile-main"
      >
        {children}
      </main>

      {showPopup && (
        <GuestSignInSheet
          onLogin={goToLanding}
          onSignup={goToLanding}
          onDismiss={() => setShowPopup(false)}
        />
      )}
    </div>
  );
}

/**
 * Left nav rail for the desktop guest view — mirrors the authed
 * `AppShell`'s sidebar (BrontoY logo, Home / Discover / Opinions /
 * Search / Notifications / Profile / Settings, Create pill, Sign-up
 * CTA at the bottom). Every item is inert for guests: the parent
 * `PublicProfileGate` intercepts the click at capture-phase and pops
 * the sign-in sheet instead of navigating.
 */
function GuestLeftNav({ onCta }) {
  const items = [
    { icon: Home, label: "Home" },
    { icon: Compass, label: "Discover" },
    { icon: MessagesSquare, label: "Opinions" },
    { icon: Search, label: "Search" },
    { icon: Bell, label: "Notifications" },
    { icon: User, label: "Profile" },
    { icon: SettingsIcon, label: "Settings" },
  ];
  return (
    <nav
      className="sticky top-0 self-start h-[100dvh] py-6 flex flex-col"
      data-testid="guest-leftnav"
    >
      <button
        onClick={onCta}
        className="flex items-center gap-2 mb-8 pl-3"
        aria-label="BrontoY home"
      >
        <img src={yLogoInline} alt="" className="w-8 h-8" />
        <span className="font-display text-2xl font-medium tracking-tight">
          BrontoY
        </span>
      </button>
      <ul className="flex flex-col gap-1">
        {items.map(({ icon: Icon, label }) => (
          <li key={label}>
            <button
              type="button"
              className="w-full flex items-center gap-4 px-3 py-3 rounded-full text-[16px] hover:bg-foreground/5 transition"
              data-testid={`guest-nav-${label.toLowerCase()}`}
            >
              <Icon className="w-6 h-6" strokeWidth={1.8} />
              <span>{label}</span>
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="mt-6 mx-3 h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition"
        data-testid="guest-nav-create"
      >
        <Plus className="w-4 h-4" />
        Create
      </button>
    </nav>
  );
}

/**
 * Right rail — a search-style input (inert, pops the sheet on
 * interaction) and the persistent "Join BrontoY today" conversion
 * card. Mirrors the authed `RightPanel`'s slots so the guest view
 * doesn't feel emptier than the real one.
 */
function GuestRightRail({ onLogin, onSignup }) {
  return (
    <div className="flex flex-col gap-5">
      <div className="relative">
        <Search
          className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
          strokeWidth={1.8}
        />
        <input
          type="text"
          placeholder="Search BrontoY"
          className="w-full h-11 rounded-full bg-secondary pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          data-testid="guest-search-input"
          readOnly
        />
      </div>
      <div
        className="rounded-3xl border border-border bg-card/60 backdrop-blur-sm p-6"
        data-testid="guest-join-card"
      >
        <img src={yLogoInline} alt="" className="w-10 h-10 mb-3" />
        <h2 className="font-display text-xl font-semibold tracking-tight mb-1">
          Join BrontoY today.
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Follow the people you love, share your own moments and views.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={onSignup}
            className="w-full h-11 rounded-full bg-foreground text-background font-semibold text-sm hover:opacity-90 transition"
            data-testid="guest-rail-signup"
          >
            Sign up
          </button>
          <button
            onClick={onLogin}
            className="w-full h-11 rounded-full border border-border font-medium text-sm hover:bg-foreground/5 transition"
            data-testid="guest-rail-login"
          >
            Log in
          </button>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground leading-relaxed px-2">
        Terms · Privacy · Community Guidelines · © 2026 BrontoY
      </div>
    </div>
  );
}

/**
 * Thin top bar for guest profile view — just the Y logo (tapping it
 * takes the guest to the Landing page). No sidebar, no right rail —
 * we deliberately keep this uncluttered so the profile is the star.
 */
function GuestTopBar({ onCta }) {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-lg bg-background/80 border-b border-border"
      data-testid="guest-topbar"
    >
      <div className="w-full max-w-[1120px] mx-auto flex items-center justify-between px-4 lg:px-6 py-3">
        <button
          onClick={onCta}
          className="flex items-center gap-2"
          data-testid="guest-topbar-logo"
          aria-label="BrontoY home"
        >
          <img src={yLogoInline} alt="" className="w-7 h-7" />
          <span className="font-display text-lg font-semibold tracking-tight">
            BrontoY
          </span>
        </button>
        <button
          onClick={onCta}
          className="h-9 px-4 rounded-full bg-foreground text-background text-xs font-semibold hover:opacity-90 transition"
          data-testid="guest-topbar-cta"
        >
          Sign up
        </button>
      </div>
    </header>
  );
}

/**
 * Guest sign-in sheet.
 *
 *   Mobile (< md, < 768px): stays a bottom-anchored card — matches the
 *     iOS spec's action sheet feel and keeps the profile visible above.
 *   Desktop (≥ md): centered modal with a dimmed full-screen backdrop
 *     so it feels like a proper dialog on wider viewports.
 *
 * Both actions lead to the pre-signup Landing at `/`.
 */
function GuestSignInSheet({ onLogin, onSignup, onDismiss }) {
  return (
    <>
      {/* Full-screen dimmed backdrop — desktop only. On mobile the
          bottom sheet floats over the profile without dimming. Clicking
          the backdrop dismisses the sheet. */}
      <button
        aria-label="Close sign in"
        onClick={onDismiss}
        className="hidden md:block fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        data-testid="guest-signin-backdrop"
      />
      <div
        className="fixed z-50 pointer-events-none inset-x-0 bottom-0 p-4 md:inset-0 md:p-6 md:flex md:items-center md:justify-center"
        data-testid="guest-signin-sheet"
      >
        <div
          className="pointer-events-auto max-w-[520px] w-full md:w-[420px] mx-auto rounded-3xl border border-border bg-card shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 md:zoom-in-95 md:slide-in-from-bottom-0 duration-200"
          role="dialog"
          aria-modal="true"
          aria-label="Sign in to BrontoY"
        >
          <div className="p-5 sm:p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3 md:justify-center md:text-center md:flex-col md:mb-6">
              <img
                src={yLogoInline}
                alt=""
                className="w-9 h-9 md:w-14 md:h-14 md:mb-2"
              />
              <div className="md:text-center">
                <div className="text-sm md:text-lg font-semibold">
                  See more on BrontoY
                </div>
                <div className="text-xs md:text-sm text-muted-foreground md:mt-1">
                  Join to follow, react and share.
                </div>
              </div>
              {/* Close (×) — mobile inline, desktop absolute so the
                  centered header stays balanced. */}
              <button
                onClick={onDismiss}
                className="ml-auto md:absolute md:top-4 md:right-4 md:ml-0 w-8 h-8 rounded-full hover:bg-foreground/5 text-muted-foreground text-lg leading-none flex items-center justify-center"
                aria-label="Dismiss"
                data-testid="guest-signin-dismiss"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 md:gap-3 mt-4 md:mt-2">
              <button
                onClick={onLogin}
                className="h-11 md:h-12 rounded-full border border-border text-sm font-medium hover:bg-foreground/5 transition"
                data-testid="guest-signin-login"
              >
                Log in
              </button>
              <button
                onClick={onSignup}
                className="h-11 md:h-12 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 transition"
                data-testid="guest-signin-signup"
              >
                Sign up
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
